import { useCallback, useEffect, useState } from "react";
import { API_URL } from "@/config";

const IST_FMT = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const btnCls =
  "rounded-[2px] border border-[#F5F2EB]/25 px-4 py-2 font-mono text-xs uppercase tracking-[0.18em] text-[#F5F2EB] transition-colors duration-200 hover:border-[#C5A059] hover:text-[#C5A059]";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("nostos_admin") || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [signups, setSignups] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [foundingOnly, setFoundingOnly] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "Nostos Admin";
    return () => {
      document.head.removeChild(meta);
      document.title = "Nostos";
    };
  }, []);

  const authed = useCallback(
    async (path, options = {}) => {
      const res = await fetch(`${API_URL}${path}`, {
        ...options,
        headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        sessionStorage.removeItem("nostos_admin");
        setToken("");
        throw new Error("Session expired");
      }
      return res;
    },
    [token]
  );

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (foundingOnly) params.set("founding", "true");
    const [listRes, statsRes] = await Promise.all([
      authed(`/admin/signups?${params.toString()}`),
      authed("/admin/stats"),
    ]);
    setSignups((await listRes.json()).signups);
    setStats(await statsRes.json());
  }, [authed, query, foundingOnly]);

  useEffect(() => {
    if (token) load().catch(() => {});
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await fetch(`${API_URL}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLoginError(typeof data.detail === "string" ? data.detail : "Login failed.");
        return;
      }
      sessionStorage.setItem("nostos_admin", data.token);
      setToken(data.token);
    } catch {
      setLoginError("Network error. Please try again.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry? Numbers are never reissued.")) return;
    await authed(`/admin/signups/${id}`, { method: "DELETE" });
    load().catch(() => {});
  };

  const exportCsv = async () => {
    const res = await authed("/admin/export");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nostos-waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    sessionStorage.removeItem("nostos_admin");
    setToken("");
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#08090A] px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm border border-[#F5F2EB]/[0.1] bg-[#0E1012] p-10"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#8C857B]">Nostos Register</p>
          <h1 className="mt-4 font-serif text-2xl font-normal tracking-tight text-[#F5F2EB]">
            Admin access
          </h1>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="mt-8 w-full rounded-[2px] border border-[#F5F2EB]/[0.12] bg-[#08090A] px-4 py-3.5 font-mono text-sm text-[#F5F2EB] outline-none transition-colors duration-200 focus:border-[rgba(197,160,89,0.5)]"
          />
          {loginError && (
            <p data-testid="admin-login-error" className="mt-4 font-mono text-xs text-[#C95A53]">
              {loginError}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            className="mt-6 w-full rounded-[2px] bg-[#F5F2EB] px-6 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.22em] text-[#08090A] transition-colors duration-200 hover:bg-[#C5A059]"
          >
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08090A] text-[#F5F2EB]">
      <header className="border-b border-[#F5F2EB]/[0.08]">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <span className="font-display text-sm font-medium tracking-[0.4em]">NOSTOS / ADMIN</span>
          <div className="flex items-center gap-3">
            <button data-testid="admin-export-csv-button" onClick={exportCsv} className={btnCls}>
              Export CSV
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className={btnCls}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        {stats && (
          <div data-testid="admin-stats" className="mb-8 flex gap-10 font-mono text-xs tracking-[0.18em] text-[#8C857B]">
            <span>
              TOTAL <span className="text-[#F5F2EB]">{stats.total}</span>
            </span>
            <span>
              FOUNDING <span className="text-[#C5A059]">{stats.founding_taken}/{stats.cap}</span>
            </span>
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-center gap-4">
          <input
            data-testid="admin-filter-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email or phone"
            className="rounded-[2px] border border-[#F5F2EB]/[0.12] bg-[#0E1012] px-4 py-2.5 font-mono text-sm text-[#F5F2EB] outline-none focus:border-[rgba(197,160,89,0.5)]"
          />
          <label className="flex cursor-pointer items-center gap-2 font-mono text-xs tracking-[0.12em] text-[#C7C2B8]">
            <input
              data-testid="admin-founding-filter-checkbox"
              type="checkbox"
              checked={foundingOnly}
              onChange={(e) => setFoundingOnly(e.target.checked)}
              className="h-4 w-4 appearance-none rounded-[2px] border border-[#F5F2EB]/30 bg-[#0E1012] checked:border-[#C5A059] checked:bg-[#C5A059]"
            />
            Founding only
          </label>
        </div>

        <div data-testid="admin-table-container" className="overflow-x-auto border border-[#F5F2EB]/[0.08]">
          <table className="w-full min-w-[960px] text-left font-mono text-xs">
            <thead>
              <tr className="border-b border-[#F5F2EB]/[0.08] text-[#8C857B]">
                <th className="px-4 py-3 font-normal tracking-[0.14em]">POS</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">FOUNDING</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">NAME</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">GENDER</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">SLEEP</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">EMAIL</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">PHONE</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">SOURCE</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">REF</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">CREATED (IST)</th>
                <th className="px-4 py-3 font-normal tracking-[0.14em]">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {signups.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`admin-row-${s.waitlist_position}`}
                  className="border-b border-[#F5F2EB]/[0.05] text-[#C7C2B8]"
                >
                  <td className="px-4 py-3 text-[#F5F2EB]">#{s.waitlist_position}</td>
                  <td className="px-4 py-3 text-[#C5A059]">
                    {s.founding_number ? `#${String(s.founding_number).padStart(3, "0")}` : ""}
                  </td>
                  <td className="px-4 py-3">{s.name}</td>
                  <td className="px-4 py-3">{s.gender}</td>
                  <td className="px-4 py-3">{s.sleep_rating != null ? `${s.sleep_rating}/10` : ""}</td>
                  <td className="px-4 py-3">{s.email}</td>
                  <td className="px-4 py-3">{s.phone}</td>
                  <td className="px-4 py-3">{s.source}</td>
                  <td className="px-4 py-3">{s.referred_by ? `#${s.referred_by}` : ""}</td>
                  <td className="px-4 py-3">{IST_FMT.format(new Date(s.created_at))}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`admin-delete-${s.waitlist_position}`}
                      onClick={() => remove(s.id)}
                      className="text-[#C95A53] transition-colors duration-200 hover:text-[#F5F2EB]"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {signups.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-[#4A4640]">
                    No entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
